import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Eye, EyeSlash, ArrowLeft } from '@phosphor-icons/react';

const formatApiError = (detail) => {
  if (!detail) return 'Une erreur est survenue. Veuillez réessayer.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
};

export const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Connexion réussie !');
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'architecte') navigate('/architect');
      else navigate('/client');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex" data-testid="login-page">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/e253916e-e5bb-46ac-bf5f-25341dda13f7/images/165d978cc44c6696f6f812c1f21c803d2c0a890df42ad902c0b00e3e8591aa05.png')` }}
        >
          <div className="absolute inset-0 bg-black/60" />
        </div>
        <div className="relative z-10 p-12 flex flex-col justify-between">
          <Link to="/" className="font-bold text-2xl tracking-tighter text-white font-['Outfit']">
            CARRÉ <span className="text-[#E53935]">CHR</span>
          </Link>
          <div>
            <h2 className="text-4xl font-light text-white tracking-tight mb-4">
              Bienvenue dans<br />votre espace
            </h2>
            <p className="text-white/60">Gérez vos dossiers de conformité en toute simplicité.</p>
          </div>
        </div>
      </div>

      {/* Right panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#52525B] hover:text-[#0A0A0A] mb-8">
            <ArrowLeft size={16} /> Retour à l'accueil
          </Link>

          <h1 className="text-3xl font-medium tracking-tight text-[#0A0A0A] mb-2">Connexion</h1>
          <p className="text-[#52525B] mb-8">Accédez à votre tableau de bord</p>

          {error && (
            <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] p-4 mb-6" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-none border-[#E5E7EB] focus:border-[#0A0A0A] h-12"
                placeholder="votre@email.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Mot de passe</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="rounded-none border-[#E5E7EB] focus:border-[#0A0A0A] h-12 pr-12"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#52525B]"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0A0A0A] hover:bg-[#27272A] rounded-none h-12"
              data-testid="login-submit-btn"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-[#52525B]">
            Pas encore de compte ?{' '}
            <Link to="/register" className="font-medium text-[#0A0A0A] hover:underline" data-testid="register-link">
              Créer un compte
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultRole = searchParams.get('role') || 'client';

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    company: '',
    phone: '',
    role: defaultRole
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);
    try {
      const { confirmPassword, ...userData } = formData;
      const user = await register(userData);
      toast.success('Compte créé avec succès !');
      if (user.role === 'architecte') navigate('/architect');
      else navigate('/client');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex" data-testid="register-page">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/e253916e-e5bb-46ac-bf5f-25341dda13f7/images/165d978cc44c6696f6f812c1f21c803d2c0a890df42ad902c0b00e3e8591aa05.png')` }}
        >
          <div className="absolute inset-0 bg-black/60" />
        </div>
        <div className="relative z-10 p-12 flex flex-col justify-between">
          <Link to="/" className="font-bold text-2xl tracking-tighter text-white font-['Outfit']">
            CARRÉ <span className="text-[#E53935]">CHR</span>
          </Link>
          <div>
            <h2 className="text-4xl font-light text-white tracking-tight mb-4">
              Rejoignez<br />CARRÉ CHR
            </h2>
            <p className="text-white/60">Créez votre compte en quelques minutes.</p>
          </div>
        </div>
      </div>

      {/* Right panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#52525B] hover:text-[#0A0A0A] mb-8">
            <ArrowLeft size={16} /> Retour à l'accueil
          </Link>

          <h1 className="text-3xl font-medium tracking-tight text-[#0A0A0A] mb-2">Créer un compte</h1>
          <p className="text-[#52525B] mb-8">Remplissez les informations ci-dessous</p>

          {error && (
            <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] p-4 mb-6" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            {/* Role selection */}
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Type de compte</label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData({...formData, role: 'client'})}
                  className={`p-4 border text-center transition-all ${formData.role === 'client' ? 'border-[#0A0A0A] bg-[#F4F4F5]' : 'border-[#E5E7EB]'}`}
                  data-testid="role-client-btn"
                >
                  <p className="font-medium">Client</p>
                  <p className="text-xs text-[#52525B]">Restaurateur</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, role: 'architecte'})}
                  className={`p-4 border text-center transition-all ${formData.role === 'architecte' ? 'border-[#0A0A0A] bg-[#F4F4F5]' : 'border-[#E5E7EB]'}`}
                  data-testid="role-architect-btn"
                >
                  <p className="font-medium">Architecte</p>
                  <p className="text-xs text-[#52525B]">Freelance</p>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Nom complet *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="rounded-none border-[#E5E7EB] h-12"
                  data-testid="register-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Téléphone</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="rounded-none border-[#E5E7EB] h-12"
                  data-testid="register-phone-input"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Entreprise</label>
              <Input
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
                className="rounded-none border-[#E5E7EB] h-12"
                placeholder="Nom de votre établissement"
                data-testid="register-company-input"
              />
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Email *</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="rounded-none border-[#E5E7EB] h-12"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Mot de passe *</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  className="rounded-none border-[#E5E7EB] h-12 pr-12"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#52525B]"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A] mb-2 block">Confirmer *</label>
              <Input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                required
                className="rounded-none border-[#E5E7EB] h-12"
                data-testid="register-confirm-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0A0A0A] hover:bg-[#27272A] rounded-none h-12"
              data-testid="register-submit-btn"
            >
              {loading ? 'Création...' : 'Créer mon compte'}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-[#52525B]">
            Déjà un compte ?{' '}
            <Link to="/login" className="font-medium text-[#0A0A0A] hover:underline" data-testid="login-link">
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
